import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { LanguageProvider } from './context/LanguageContext';
import { ThemeProvider } from './context/ThemeContext';
import Navbar from './components/Navbar';
import LandingPage from './components/Home/LandingPage';
import ArticlesPage from './components/Home/ArticlesPage';
import ContactPage from './components/Home/ContactPage';
import IntakePage from './components/Home/IntakePage';
import ServiceDetailPage from './components/Home/ServiceDetailPage';
import LoginPage from './components/Auth/LoginPage';
import ProfilePage from './components/Profile/ProfilePage';
import AdminDashboard from './components/Admin/AdminDashboard';
import LawyerDashboard from './components/Lawyer/LawyerDashboard';
import ClientDashboard from './components/Client/ClientDashboard';
import RouteSeo from './components/Seo/RouteSeo';
import Footer from './components/Footer';
import { trackPageView } from './firebase/config';
import './App.css';
import './components/Home/IntakePage.css';

let lastTrackedPageView = {
  key: '',
  timestamp: 0
};

const ProtectedRoute = ({ children, allowedRoles }: { children: React.ReactNode, allowedRoles: string[] }) => {
  const { user, role, accountStatus, loading } = useAuth();
  if (loading) return <div>Loading...</div>;
  if (!user) return <Navigate to="/login" />;
  if (role === 'lawyer' && accountStatus !== 'approved') return <Navigate to="/login" />;
  if (allowedRoles && role && !allowedRoles.includes(role)) return <Navigate to="/" />;
  return <>{children}</>;
};

const AnalyticsTracker = () => {
  const location = useLocation();

  React.useEffect(() => {
    const pageKey = `${location.pathname}${location.search}${location.hash}|${document.title}`;
    const now = Date.now();

    if (
      lastTrackedPageView.key === pageKey &&
      now - lastTrackedPageView.timestamp < 1000
    ) {
      return;
    }

    lastTrackedPageView = {
      key: pageKey,
      timestamp: now
    };

    void trackPageView(
      `${location.pathname}${location.search}${location.hash}`,
      document.title
    );
  }, [location.hash, location.pathname, location.search]);

  return null;
};

function App() {
  return (
    <LanguageProvider>
      <ThemeProvider>
        <AuthProvider>
          <Router>
            <RouteSeo />
            <AnalyticsTracker />
            <div className="app-container">
              <Navbar />
              <main className="main-content">
                <Routes>
                  <Route path="/" element={<LandingPage />} />
                  <Route path="/articles" element={<ArticlesPage />} />
                  <Route path="/contact" element={<ContactPage />} />
                  <Route path="/intake" element={<IntakePage />} />
                  <Route path="/services/:serviceId" element={<ServiceDetailPage />} />
                  <Route path="/login" element={<LoginPage />} />
                  <Route
                    path="/profile"
                    element={
                      <ProtectedRoute allowedRoles={['admin', 'lawyer', 'client']}>
                        <ProfilePage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/profile/:userId"
                    element={
                      <ProtectedRoute allowedRoles={['admin', 'lawyer', 'client']}>
                        <ProfilePage />
                      </ProtectedRoute>
                    }
                  />

                  <Route 
                    path="/admin/*" 
                    element={
                      <ProtectedRoute allowedRoles={['admin']}>
                        <AdminDashboard />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/lawyer/*" 
                    element={
                      <ProtectedRoute allowedRoles={['lawyer']}>
                        <LawyerDashboard />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/client/*" 
                    element={
                      <ProtectedRoute allowedRoles={['client']}>
                        <ClientDashboard />
                      </ProtectedRoute>
                    } 
                  />
                </Routes>
              </main>
              <Footer />
            </div>
          </Router>
        </AuthProvider>
      </ThemeProvider>
    </LanguageProvider>
  );
}

export default App;
